import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { PreferredSourceButton } from "@/components/site/PreferredSourceButton";
import { ArticleCard, ArticleRow, FeatureCard } from "@/components/site/ArticleCard";
import { listCategories, listPublishedArticles } from "@/lib/articles.functions";
// Lazy — admin-only status strip. Pulls the Supabase browser client
// (Realtime + Phoenix) which would otherwise sit in the main bundle for
// every public visitor.
const AutopilotStrip = lazy(() =>
  import("@/components/site/AutopilotStrip").then((m) => ({ default: m.AutopilotStrip })),
);
import { SITE } from "@/lib/site";
import { transformCoverUrl } from "@/lib/cover-image-url";
import ogHomeFallback from "@/assets/og-home.jpg";

export const Route = createFileRoute("/")({
  head: ({ loaderData }) => {
    const data = loaderData as
      | { articles: { slug: string; title: string }[]; heroCoverUrl: string | null }
      | undefined;
    const articles = data?.articles;
    const heroCoverUrl = data?.heroCoverUrl ?? null;
    const title = `${SITE.name} — Local SEO, Google Maps & Business Profile News`;
    // Prefer the current lead story's cover as the social share image so
    // Product Hunt / Slack / LinkedIn / X previews always reflect the latest
    // reporting instead of falling back to a headless-browser screenshot.
    const heroOgImage = heroCoverUrl
      ? transformCoverUrl(heroCoverUrl, { width: 1200 }) ?? heroCoverUrl
      : null;
    const ogImage = heroOgImage ?? `${SITE.url}${ogHomeFallback}`;
    const ogImageAlt = heroCoverUrl
      ? `${SITE.name} — latest reporting on Google Maps, Google Business Profile & local search`
      : `${SITE.name} — Local search, decoded`;
    return {
      meta: [
        { title },
        { name: "description", content: SITE.description },
        { property: "og:title", content: title },
        { property: "og:description", content: SITE.description },
        { property: "og:url", content: `${SITE.url}/` },
        { property: "og:image", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: ogImageAlt },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: SITE.description },
        { name: "twitter:image", content: ogImage },
        { name: "twitter:image:alt", content: ogImageAlt },
      ],
      links: [
        { rel: "canonical", href: `${SITE.url}/` },
        { rel: "alternate", hrefLang: "en", href: `${SITE.url}/` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE.url}/` },
        ...(heroCoverUrl
          ? [{
              rel: "preload",
              as: "image",
              href: transformCoverUrl(heroCoverUrl, { width: 960 }) ?? heroCoverUrl,
            } as any]
          : []),
      ],
      scripts: articles && articles.length > 0
        ? [{
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: "Latest articles",
              itemListElement: articles.slice(0, 10).map((a, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `${SITE.url}/article/${a.slug}`,
                name: a.title,
              })),
            }),
          }]
        : [],
    };
  },

  loader: async ({ context }) => {
    const [resp] = await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["articles", "home"],
        queryFn: () => listPublishedArticles({ data: { limit: 24 } }),
      }),
      context.queryClient.prefetchQuery({
        queryKey: ["categories"],
        queryFn: () => listCategories(),
      }),
    ]);
    return {
      articles: resp.articles.map((a) => ({ slug: a.slug, title: a.title })),
      heroCoverUrl: resp.articles[0]?.cover_image_url ?? null,
    };
  },
  component: Index,
});

function HomeContent() {
  const { data: articlesResp } = useSuspenseQuery({
    queryKey: ["articles", "home"],
    queryFn: () => listPublishedArticles({ data: { limit: 24 } }),
  });
  const { data: categories } = useSuspenseQuery({
    queryKey: ["categories"],
    queryFn: () => listCategories(),
  });

  const articles = articlesResp.articles;
  const catMap = new Map(categories.map((c) => [c.id, { slug: c.slug, name: c.name }] as const));

  const [hero, ...rest] = articles;
  const topThree = rest.slice(0, 3);
  const next = rest.slice(3, 9);
  const tail = rest.slice(9);

  if (!hero) {
    return (
      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-24 text-center">
        <p className="eyebrow">Issue No. 001</p>
        <h2 className="headline-display mt-4 text-5xl">The first edition is on the press.</h2>
        <p className="mt-6 text-lg text-muted-foreground">
          Map Observer launches with original reporting on Google Maps, Google Business Profile, and
          local SEO. Subscribe to the Friday brief and we'll send the inaugural stories to your inbox.
        </p>
        <div className="mt-10">
          <NewsletterCTA />
        </div>
      </section>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <h1 className="sr-only">Map Observer — Google Business Profile, Map Pack & Local SEO News for Agencies and Local Business Owners</h1>
      <div className="border-b border-border py-3 text-center text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        Read by local SEO agencies · GBP specialists · multi-location operators · local business owners
      </div>
      <Suspense fallback={null}>
        <AutopilotStrip />
      </Suspense>
      {/* Hero feature */}
      <section className="py-10 sm:py-14 border-b border-border">
        <FeatureCard article={hero} categories={catMap} />
      </section>

      {/* Top three under hero */}
      {topThree.length > 0 && (
        <section className="py-12 grid gap-10 md:grid-cols-3 border-b border-border">
          {topThree.map((a) => (
            <ArticleCard key={a.id} article={a} categories={catMap} />
          ))}
        </section>
      )}

      {/* Start here — evergreen guides */}
      <section className="py-12 border-b border-border">
        <div className="flex items-end justify-between border-b border-foreground pb-3 mb-6">
          <h2 className="headline-display text-2xl">Start here</h2>
          <Link to="/guide" className="eyebrow hover:text-accent">All guides →</Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { slug: "map-pack", title: "What is the Map Pack?", eyebrow: "Guide" },
            { slug: "google-business-profile", title: "Google Business Profile, explained", eyebrow: "Guide" },
            { slug: "local-seo", title: "Local SEO in 2026", eyebrow: "Pillar" },
            { slug: "ai-search-local", title: "AI search for local", eyebrow: "Guide" },
          ].map((g) => (
            <Link
              key={g.slug}
              to="/guide/$slug"
              params={{ slug: g.slug }}
              className="block border border-border p-4 hover:border-foreground transition-colors"
            >
              <p className="eyebrow">{g.eyebrow}</p>
              <p className="mt-2 font-semibold leading-snug">{g.title}</p>
            </Link>
          ))}
        </div>
      </section>

      <NewsletterCTA />

      <PreferredSourceButton placement="home" className="mt-10" />


      {/* Section heading */}
      {next.length > 0 && (
        <section className="pt-2 pb-12">
          <div className="flex items-end justify-between border-b border-foreground pb-3 mb-8">
            <h2 className="headline-display text-3xl">More from the desk</h2>
            <span className="eyebrow hidden sm:inline">Latest reporting</span>
          </div>
          <div className="grid gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
            {next.map((a) => (
              <ArticleCard key={a.id} article={a} categories={catMap} variant="compact" />
            ))}
          </div>
        </section>
      )}

      {/* Long tail rows */}
      {tail.length > 0 && (
        <section className="pb-16">
          <div className="flex items-end justify-between border-b border-foreground pb-3 mb-2">
            <h2 className="headline-display text-3xl">The archive</h2>
            <span className="eyebrow hidden sm:inline">Earlier stories</span>
          </div>
          <div>
            {tail.map((a) => (
              <ArticleRow key={a.id} article={a} categories={catMap} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Index() {
  return (
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <main id="main">
        <Suspense fallback={<div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 text-center text-muted-foreground">Loading the latest…</div>}>
          <HomeContent />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}
